import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { OnlyStringKeys } from '@eluve/utils';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';
import { cn } from '../utils/cn';

import { TableSearch } from './TableSearch';
import { useTextHighlight } from './useTextHighlight';

/**
 * Optional per-column className overrides, read off `columnDef.meta`. Lets a
 * column control its `<th>` / `<td>` classes (e.g. `w-full` to make one column
 * absorb slack so the rest shrink to content). Purely additive — columns that
 * don't set these are unaffected.
 */
interface ColumnClassNameMeta {
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isPaginated?: boolean;
  enableGlobalSearch?: boolean;
  initialSortingState?: { id: OnlyStringKeys<keyof TData>; desc: boolean }[];
  placeholder?: string;
  autoResetPageIndex?: boolean;
  /**
   * Optional classes merged onto the outer wrapper. Purely additive — omit it
   * and the default `rounded-md border` frame is unchanged. Pass e.g.
   * `border-0 rounded-none` to let a parent Card be the sole frame.
   */
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isPaginated = true,
  enableGlobalSearch,
  initialSortingState = [],
  placeholder,
  autoResetPageIndex,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(initialSortingState);
  const [globalSearch, setGlobalSearch] = useState<string>('');

  const tableBodyRef = useTextHighlight(globalSearch);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: isPaginated ? getPaginationRowModel() : undefined,
    getFilteredRowModel: enableGlobalSearch ? getFilteredRowModel() : undefined,
    autoResetPageIndex,
    state: {
      sorting,
      globalFilter: enableGlobalSearch ? globalSearch : undefined,
    },
  });

  return (
    <div
      className={cn(
        'relative w-full overflow-clip rounded-md border',
        className,
      )}
    >
      {enableGlobalSearch && (
        <div className="sticky top-0 z-10 bg-bg-primary">
          <TableSearch value={globalSearch} onChange={setGlobalSearch} />
        </div>
      )}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | ColumnClassNameMeta
                  | undefined;
                return (
                  <TableHead key={header.id} className={meta?.headerClassName}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody ref={tableBodyRef}>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as
                    | ColumnClassNameMeta
                    | undefined;
                  return (
                    <TableCell key={cell.id} className={meta?.cellClassName}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {placeholder ?? 'No results.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isPaginated && (
        <Stack
          className="sticky bottom-0 border-t border-border-primary bg-bg-primary p-2 text-sm sm:justify-end"
          direction="horizontal"
          gap={6}
          justify="between"
        >
          <label className="flex flex-row items-center gap-3 whitespace-nowrap">
            <span className="text-xs font-medium text-text-primary">
              Page size
            </span>
            <Select
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
              defaultValue={table.getState().pagination.pageSize.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select page size" className="w-max">
                  {table.getState().pagination.pageSize}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <Icon name="ArrowLeft" className="md:mr-2" />
              <span className="hidden text-xs font-medium text-text-primary md:block">
                Previous
              </span>
            </Button>
            {table.getState().pagination.pageIndex + 1}
            <span className="text-text-disabled">
              {' '}
              / {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="hidden text-xs font-medium text-text-primary md:block">
                Next
              </span>
              <Icon name="ArrowRight" className="md:ml-2" />
            </Button>
          </Stack>
        </Stack>
      )}
    </div>
  );
}
